import React, { Fragment, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Field } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { OnChange } from "react-final-form-listeners";
import { Block, Button, List, ListItem, ListInput, Popover } from "framework7-react";
import _ from "lodash";

import { InputField, SmartSelect, ToggleField, required, isNotEmpty } from "modules/4_final_form/mobile";
import { getEmployees } from "modules/employees/actions";

const blankLine = () => ({ description: "", quantity: 1 });

const NotesPopoverField = ({ input }) => {
  const [opened, setOpened] = useState(false);
  const preview = input.value ? _.truncate(input.value, { length: 28 }) : "Tap to add";

  return (
    <Fragment>
      <ListItem
        id="store-request-notes"
        title="Notes"
        after={preview}
        link="#"
        slot="list"
        onClick={(e) => {
          e.preventDefault();
          setOpened(true);
        }}
      />
      <Popover
        targetEl="#store-request-notes"
        opened={opened}
        onPopoverClosed={() => setOpened(false)}
        style={{ width: "90vw", maxWidth: 360 }}
      >
        <List className="no-margin">
          <ListInput
            type="textarea"
            placeholder="Notes"
            value={input.value || ""}
            onChange={(e) => input.onChange(e.target.value)}
            resizable
          />
        </List>
        <Block className="no-margin padding-half">
          <Button fill size="sm" popoverClose>
            Done
          </Button>
        </Block>
      </Popover>
    </Fragment>
  );
};

const LineItemsFields = () => (
  <FieldArray name="line_items_attributes" validate={isNotEmpty}>
    {({ fields, meta: { error, touched } }) => (
      <Fragment>
        <List className="no-margin-top">
          <ListItem title="Line Items" groupTitle />
          {fields.map((field, index) => {
            if (fields.value[index]?._destroy) return null;
            return (
              <Fragment key={field}>
                <Field
                  component={InputField}
                  name={`${field}.description`}
                  label="Item"
                  validate={required}
                  parse={(v) => v}
                  placeholder="What do you need?"
                  inlineLabel
                  slot="list"
                />
                <Field
                  component={InputField}
                  name={`${field}.quantity`}
                  type="number"
                  label="Qty"
                  validate={required}
                  inlineLabel
                  slot="list"
                />
                {fields.length > 1 && (
                  <Button
                    fill
                    color="red"
                    size="sm"
                    className="margin-horizontal margin-bottom-half"
                    slot="list"
                    onClick={() => {
                      const row = fields.value[index];
                      if (row?.id) {
                        fields.update(index, { ...row, _destroy: true });
                      } else {
                        fields.remove(index);
                      }
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Fragment>
            );
          })}
        </List>

        <Block className="padding-half no-margin-top">
          <Button fill color="blue" size="sm" onClick={() => fields.push(blankLine())}>
            Add Line
          </Button>
          {_.isString(error) && touched && <div className="text-danger margin-top-half">{error}</div>}
        </Block>
      </Fragment>
    )}
  </FieldArray>
);

function mapStateToProps(state) {
  return {
    employees: state.employees,
  };
}

const StoreRequestForm = (props) => {
  const { mw_jobs, employees, values, form } = props;
  const toolAccount = !!values?.tool_account;

  useEffect(() => {
    if (_.isEmpty(employees)) {
      props.getEmployees();
    }
  }, []);

  const jobOptions = useMemo(
    () =>
      _.map(_.sortBy(mw_jobs, "code"), (mw_job) => ({
        label: `${mw_job.code} - ${mw_job.description}`,
        value: mw_job.code,
      })),
    [mw_jobs],
  );

  const employeeOptions = useMemo(
    () =>
      _.map(_.sortBy(employees, ["last_name", "first_name"]), (emp) => ({
        value: emp.id,
        label: emp.display_name,
      })),
    [employees],
  );

  return (
    <form>
      <List className="no-margin-bottom">
        <Field component={ToggleField} name="tool_account" label="Tool Account" slot="list" />
        <OnChange name="tool_account">
          {(value) => {
            if (value) {
              form.change("mw_job_id", null);
            } else {
              form.change("tool_account_employee_id", null);
            }
          }}
        </OnChange>

        {!toolAccount && (
          <Field
            name="mw_job_id"
            component={SmartSelect}
            options={jobOptions}
            validate={required}
            virtualList
            label="Project"
            placeholder="Select Job"
            valueName="value"
            labelName="label"
            slot="list"
            searchbar
            searchbarPlaceholder="Search Job"
          />
        )}

        {toolAccount && (
          <Field
            name="tool_account_employee_id"
            component={SmartSelect}
            options={employeeOptions}
            validate={required}
            label="Employee"
            placeholder="Select Employee"
            valueName="value"
            labelName="label"
            slot="list"
            searchbar
            searchbarPlaceholder="Search Employee"
          />
        )}

        <Field name="notes" parse={(v) => v} component={NotesPopoverField} />
      </List>

      <LineItemsFields />
    </form>
  );
};

export default connect(mapStateToProps, { getEmployees })(StoreRequestForm);
